import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, Link2 } from 'lucide-react';
import { Button } from './@/components/ui/button';
import { Input } from './@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './@/components/ui/select';
import { displayRole } from './roleLabel';
import './ConfigPanel.css';

const SchedulingLinksPanel = ({ team, schedulingLinks, setSchedulingLinks, partnerId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newLink, setNewLink] = useState({
    type: 'containment',
    sourceEmployeeId: '',
    targetEmployeeId: '',
    weight: 100,
  });
  const [validationError, setValidationError] = useState('');

  const activeTeam = (team || []).filter(m => m.Active !== false);

  const getEmployeeName = (id) => {
    const member = activeTeam.find(m => m.Id === id);
    return member ? member.Name : id;
  };

  const handleAdd = () => {
    if (!newLink.sourceEmployeeId || !newLink.targetEmployeeId) {
      setValidationError('Select both source and target employees.');
      return;
    }
    if (newLink.sourceEmployeeId === newLink.targetEmployeeId) {
      setValidationError('Source and target must be different employees.');
      return;
    }
    setValidationError('');
    setSchedulingLinks([
      ...schedulingLinks,
      { ...newLink, weight: Number(newLink.weight) || 100 },
    ]);
    setNewLink({ type: 'containment', sourceEmployeeId: '', targetEmployeeId: '', weight: 100 });
    setIsAdding(false);
  };

  const handleDelete = (index) => {
    setSchedulingLinks(schedulingLinks.filter((_, i) => i !== index));
  };

  return (
    <div className="collapsible-section">
      <div className="collapsible-header" onClick={() => setIsOpen(!isOpen)}>
        <h2 className="collapsible-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link2 size={16} />
          Scheduling Links {schedulingLinks.length > 0 && `(${schedulingLinks.length})`}
        </h2>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {isOpen && (
        <div className="config-panel-content">
          {schedulingLinks.length === 0 && !isAdding && (
            <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
              No scheduling links defined. Links enforce pairwise constraints between employees in the LP.
            </p>
          )}

          {schedulingLinks.map((link, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                marginBottom: 8,
                backgroundColor: 'white',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                borderLeftWidth: '4px',
                borderLeftColor: link.type === 'containment' ? '#3b82f6' : '#ef4444',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                fontSize: '0.85rem',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  backgroundColor: link.type === 'containment' ? '#3b82f6' : '#ef4444',
                  color: '#fff',
                }}
              >
                {link.type}
              </span>
              <span style={{ color: '#1e293b' }}>
                {getEmployeeName(link.sourceEmployeeId)}
                {link.type === 'containment' ? ' \u2192 ' : ' \u2194 '}
                {getEmployeeName(link.targetEmployeeId)}
              </span>
              <span style={{ color: '#6b7280', marginLeft: 'auto', fontSize: '0.8rem' }}>
                w={link.weight}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(idx)}
                style={{ padding: 4, height: 'auto' }}
              >
                <Trash2 size={14} color="#dc2626" />
              </Button>
            </div>
          ))}

          {isAdding ? (
            <div
              style={{
                padding: 12,
                backgroundColor: '#f8fafc',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                marginTop: 8,
              }}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', maxWidth: 700 }}>
                <div style={{ width: 130 }}>
                  <label style={{ fontSize: '0.75rem', color: '#475569', display: 'block', marginBottom: 4 }}>Type</label>
                  <Select value={newLink.type} onValueChange={(v) => setNewLink({ ...newLink, type: v })}>
                    <SelectTrigger style={{ height: 32 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="containment">Containment</SelectItem>
                      <SelectItem value="exclusion">Exclusion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ width: 200 }}>
                  <label style={{ fontSize: '0.75rem', color: '#475569', display: 'block', marginBottom: 4 }}>
                    {newLink.type === 'containment' ? 'Source (if on-site...)' : 'Employee A'}
                  </label>
                  <Select
                    value={newLink.sourceEmployeeId}
                    onValueChange={(v) => { setNewLink({ ...newLink, sourceEmployeeId: v }); setValidationError(''); }}
                  >
                    <SelectTrigger style={{ height: 32 }}>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTeam.map(m => (
                        <SelectItem key={m.Id} value={m.Id}>{m.Name} ({displayRole(m.Role_Type, partnerId)})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ width: 200 }}>
                  <label style={{ fontSize: '0.75rem', color: '#475569', display: 'block', marginBottom: 4 }}>
                    {newLink.type === 'containment' ? 'Target (...must also be)' : 'Employee B'}
                  </label>
                  <Select
                    value={newLink.targetEmployeeId}
                    onValueChange={(v) => { setNewLink({ ...newLink, targetEmployeeId: v }); setValidationError(''); }}
                  >
                    <SelectTrigger style={{ height: 32 }}>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeTeam.map(m => (
                        <SelectItem key={m.Id} value={m.Id}>{m.Name} ({displayRole(m.Role_Type, partnerId)})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ width: 70 }}>
                  <label style={{ fontSize: '0.75rem', color: '#475569', display: 'block', marginBottom: 4 }}>Weight</label>
                  <Input
                    type="number"
                    value={newLink.weight}
                    onChange={(e) => setNewLink({ ...newLink, weight: e.target.value })}
                    style={{ height: 32 }}
                  />
                </div>
              </div>

              {validationError && (
                <p style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 8 }}>{validationError}</p>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Button size="sm" onClick={handleAdd} style={{ backgroundColor: '#059669', color: '#fff' }}>Add</Button>
                <Button size="sm" variant="outline" onClick={() => { setIsAdding(false); setValidationError(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAdding(true)}
              style={{ marginTop: 4 }}
            >
              <Plus size={14} style={{ marginRight: 6 }} /> Add Link
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SchedulingLinksPanel;
